import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NbToastrService } from '@nebular/theme';
import { TwoFactorService, TwoFactorStatus } from '../../../services/two-factor.service';

type Stage = 'loading' | 'overview' | 'scan' | 'codes';

/**
 * Enrol, inspect and remove the second factor.
 *
 * Used in two places with the same code. In the settings page it manages an
 * existing session. On the forced-enrolment page it runs with `forced=true`,
 * where there is no session at all — only the short-lived enrolment token the
 * login handed to `TwoFactorService` — so the overview and the disable button
 * are hidden and the user goes straight into scanning.
 */
@Component({
  selector: 'app-two-factor-form',
  templateUrl: './two-factor-form.component.html',
  styleUrl: './two-factor-form.component.scss',
  standalone: false,
})
export class TwoFactorFormComponent implements OnInit {
  /** True on the forced-enrolment page: no session, no way back, no opt-out. */
  @Input() forced = false;
  /** Fires once the factor is active and the user has seen the codes. */
  @Output() completed = new EventEmitter<void>();

  stage: Stage = 'loading';
  status: TwoFactorStatus | null = null;

  qr: SafeHtml | null = null;
  secret = '';
  code = '';
  busy = false;
  error: string | null = null;

  /** Shown exactly once, right after activation. Never fetchable again. */
  recoveryCodes: string[] = [];
  codesAcknowledged = false;

  // Disable flow
  disabling = false;
  disablePassword = '';
  disableCode = '';

  constructor(
    private twoFactor: TwoFactorService,
    private sanitizer: DomSanitizer,
    private toastr: NbToastrService,
  ) {}

  /** Error bodies come in two shapes: a plain string for ordinary failures
   *  and an object for the throttle (`{code, retry_after}`). Rendering the
   *  object directly would print "[object Object]" at the user. */
  private describe(e: any, fallback: string): string {
    const detail = e?.error?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object') {
      if (detail.code === 'too_many_code_attempts' || detail.code === 'too_many_attempts') {
        const minutes = Math.max(1, Math.ceil((detail.retry_after || 60) / 60));
        return `Too many attempts. Wait ${minutes} minute(s) and try again.`;
      }
    }
    return fallback;
  }


  /** "Your organisation requires it" is wrong for a super_admin, whose
   *  requirement comes from the platform and ignores the tenant switch. */
  private get byPlatform(): boolean {
    return this.status?.required_reason === 'platform';
  }

  get requiredHintKey(): string {
    return this.byPlatform
      ? 'pages.twofa.requiredHintPlatform'
      : 'pages.twofa.requiredHint';
  }

  get reenrolHintKey(): string {
    return this.byPlatform
      ? 'pages.twofa.requiredReenrolHintPlatform'
      : 'pages.twofa.requiredReenrolHint';
  }


  async ngOnInit(): Promise<void> {
    if (this.forced) {
      await this.beginSetup();
      return;
    }
    await this.refresh();
  }

  async refresh(): Promise<void> {
    this.stage = 'loading';
    try {
      this.status = await this.twoFactor.status();
      this.stage = 'overview';
    } catch {
      this.error = 'Could not read the two-factor status.';
      this.stage = 'overview';
    }
  }

  /** The one switch. On starts the setup wizard, off opens the confirm step.
   *
   *  The toggle is bound to `status.enabled`, never to a local flag, so it
   *  snaps back on its own if the user abandons either flow — it must never
   *  show a state the server has not accepted.
   */
  async onToggle(next: boolean): Promise<void> {
    if (this.busy) {
      return;
    }
    if (next) {
      await this.beginSetup();
    } else {
      this.startDisable();
    }
  }


  async beginSetup(): Promise<void> {
    this.busy = true;
    this.error = null;
    try {
      const setup = await this.twoFactor.setup();
      this.secret = setup.secret;
      // The SVG comes from our own backend, not from user input.
      this.qr = this.sanitizer.bypassSecurityTrustHtml(setup.qr_svg);
      this.code = '';
      this.stage = 'scan';
    } catch (e: any) {
      this.error = this.describe(e, 'Could not start the setup.');
      this.stage = this.forced ? 'scan' : 'overview';
    } finally {
      this.busy = false;
    }
  }

  async activate(): Promise<void> {
    if (this.busy || !this.code.trim()) {
      return;
    }
    this.busy = true;
    this.error = null;
    try {
      this.recoveryCodes = await this.twoFactor.activate(this.code.trim());
      this.codesAcknowledged = false;
      this.stage = 'codes';
    } catch (e: any) {
      this.error = this.describe(e, 'That code was not accepted.');
      this.code = '';
    } finally {
      this.busy = false;
    }
  }

  copyCodes(): void {
    navigator.clipboard?.writeText(this.recoveryCodes.join('\n')).then(
      () => this.toastr.success('Recovery codes copied.', 'Two-factor'),
      () => this.toastr.warning('Copying failed — write them down instead.', 'Two-factor'),
    );
  }

  downloadCodes(): void {
    const blob = new Blob(
      [
        'AlphaLearn recovery codes\n',
        'Each code works once. Keep them somewhere you can reach without your phone.\n\n',
        this.recoveryCodes.join('\n'),
        '\n',
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'alphalearn-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  async finishCodes(): Promise<void> {
    this.recoveryCodes = [];
    if (this.forced) {
      this.twoFactor.endForcedEnrollment();
      this.completed.emit();
      return;
    }
    await this.refresh();
    this.completed.emit();
  }

  startDisable(): void {
    this.disabling = true;
    this.disablePassword = '';
    this.disableCode = '';
    this.error = null;
  }

  cancelDisable(): void {
    this.disabling = false;
    this.disablePassword = '';
    this.disableCode = '';
  }

  async confirmDisable(): Promise<void> {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.error = null;
    try {
      await this.twoFactor.disable(this.disablePassword, this.disableCode);
      this.disabling = false;
      this.toastr.success('Two-factor authentication is off.', 'Two-factor');
      await this.refresh();
    } catch (e: any) {
      this.error = this.describe(e, 'Could not switch it off.');
    } finally {
      this.busy = false;
    }
  }

  async regenerate(): Promise<void> {
    if (this.busy || !this.code.trim()) {
      return;
    }
    this.busy = true;
    this.error = null;
    try {
      this.recoveryCodes = await this.twoFactor.regenerateRecoveryCodes(this.code.trim());
      this.codesAcknowledged = false;
      this.code = '';
      this.stage = 'codes';
    } catch (e: any) {
      this.error = this.describe(e, 'That code was not accepted.');
    } finally {
      this.busy = false;
    }
  }
}
